import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import styled from "@mui/material/styles/styled";
import Fade from "@mui/material/Fade/Fade";
import { QuestionElement } from "./Questions/questionElement";
import { AnswerElement } from "./answers/answerElement";
import { CHAT_ELEMENT_SPACING } from "./consts/sizeConsts";
import {
  ConversationQuestionType,
  ConversationQuestionType as QTypes,
  QualifiedFeature,
} from "fe-common/graphql";
import {
  ConversationDriverExchange,
  ExchangeAnswer,
} from "../driver/types/driverTypes";
import { refreshRolesForDemoSignUp } from "../hooks/useRefreshRoles";
import { RefreshElement } from "./refreshElement";
import {
  CHECK_MY_SYMPTOMS_FEATURE_ID,
  FREE_TEXT_COMPLAINT_FEATURE_ID,
  GenderValue,
} from "shared";
import { Answer } from "../solverHooks/useConversation";
import { DriverQuestionsConfig } from "../driver/questions/Question";
import { useClinicData, useClinicShortCode } from "../clinic/clinicHooks";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import { InfoMessageView } from "./infoMessages/infoMessageView";
import { DocQuestion } from "./Questions/DocQuestion";
import { PatientColorMap } from "../layout/patientColorMap";
import { ThreeDotsTypingLoader } from "fe-web-components/loader/threeDotsTypingLoader";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { ServerErrorHandlerComponent } from "./serverErrors/ServerErrorHandlerComponent";

//region [[ Styles ]]

export const ChatElementContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  "&:not(:last-of-type)": {
    margin: `0px 0px ${CHAT_ELEMENT_SPACING}px 0px`,
  },
}));

const LoadingContainer = styled(ChatElementContainer)({
  flexDirection: "row",
  maxHeight: "44px",
  marginBottom: "12px",
  width: "fit-content",
  "& .doc-question": {
    flexDirection: "row",
  },
  "& p.question-text": {
    display: "none",
  },
});

const Loader = styled(ThreeDotsTypingLoader)({
  width: "100%",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  margin: "0 12px",
  "& .anchor": {
    marginTop: "0",
  },
  paddingRight: "12px",
  ...onRtl({
    paddingRight: "auto",
    paddingLeft: "12px",
  }),
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ChatElementProps {
  index: number;
  exchange: ConversationDriverExchange;
  onAnswer: (answer: ExchangeAnswer) => void;
  completeInfoMessages: () => void;
  undo: () => void;
  gender: GenderValue;
  loading: boolean;
  exchanges?: ConversationDriverExchange[];
}

//endregion [[ Props ]]

//region [[ Functions ]]

function exchangeToAnswer(
  questionType: ConversationQuestionType,
  exchangeAnswer: ExchangeAnswer,
) {
  if (!questionType || !exchangeAnswer) return null;

  return {
    type: questionType,
    qfs: exchangeAnswer?.qfs,
    group: (
      DriverQuestionsConfig[questionType] ||
      DriverQuestionsConfig[ConversationQuestionType.Suggestion]
    ).group,
    source: (
      DriverQuestionsConfig[questionType] ||
      DriverQuestionsConfig[ConversationQuestionType.Suggestion]
    ).source,
    skipped: exchangeAnswer?.skip,
  };
}

function shouldCreateCode(exchanges: ConversationDriverExchange[]): boolean {
  if (codeCreated) return false;

  const visitReasonExchange = exchanges.find(
    (e) => e.type === ConversationQuestionType.ReasonForVisit,
  );
  const isCheckMySymptoms = visitReasonExchange?.answer?.qfs
    ?.filter((qf) => !qf.negate && !qf.skip)
    .some((qf) => qf.feature.id === CHECK_MY_SYMPTOMS_FEATURE_ID);

  const freeTextComplaintAnswered = exchanges.some((e) =>
    e.answer?.qfs?.some(
      (qf) =>
        qf.feature.id === FREE_TEXT_COMPLAINT_FEATURE_ID &&
        qf.qualifiers.some((q) => q.freeText),
    ),
  );

  const ccAnswered =
    exchanges.some(
      (e) =>
        e.type === ConversationQuestionType.ChiefComplaint ||
        e.type === ConversationQuestionType.MultipleChiefComplaint,
    ) || freeTextComplaintAnswered;

  const freeTextAnswered = exchanges.some(
    (e) =>
      e.type === ConversationQuestionType.FreeText ||
      e.type === ConversationQuestionType.FreeTextShort,
  );

  if (!visitReasonExchange || isCheckMySymptoms) {
    //no visit reason OR check my symptoms --> wait for CC answer
    return ccAnswered;
  } else {
    //there is a visit reason which is not check my symptoms --> wait for free text
    return freeTextAnswered;
  }
}

//endregion [[ Functions ]]
let codeCreated = false;
export const ChatElementView = React.memo(
  forwardRef((props: ChatElementProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      exchange,
      index,
      onAnswer,
      completeInfoMessages,
      undo,
      gender,
      exchanges,
    } = props;
    const createVisitRecord = usePatientFeatureFlags(
      "createVisitRecord",
    ) as boolean;

    const { isClinicMode } = useClinicData();
    const { makeCodeForCurrentSession } = useClinicShortCode();
    const { nonPersist } = useWidgetSettings();
    const [infoIndexToShow, setInfoIndexToShow] = useState(0);

    const wasAnswered = !!exchange?.answer;

    useEffect(() => {
      if (exchange) {
        setInfoIndexToShow(0);
      }
    }, [exchange]);

    useEffect(() => {
      (async () => {
        if (
          exchange?.info?.[infoIndexToShow] &&
          !exchange?.info?.[infoIndexToShow]?.confirm
        ) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); //delay of 1 second in between inline messages
          setInfoIndexToShow((prev) => prev + 1);
        }

        //When approving the last info continue to next exchange
        if (
          exchange?.info?.length &&
          infoIndexToShow >= exchange?.info?.length
        ) {
          completeInfoMessages();
        }
      })();
    }, [infoIndexToShow, exchange, completeInfoMessages]);

    const createCode =
      (isClinicMode || createVisitRecord) &&
      !nonPersist &&
      shouldCreateCode(exchanges);

    const onAnswerCb = useCallback(
      (type: QTypes) => (
        qfs: QualifiedFeature[],
        skipped: boolean,
        metadata: any,
      ) => {
        refreshRolesForDemoSignUp();
        if (createCode) {
          //this replaces the same logic that was after the reason for visit,
          //now we will create a code and set "finished" (which is actually "started" now) after the first answer
          makeCodeForCurrentSession().then((res) => {
            // console.log("code created");
            if (res) {
              codeCreated = true;
            }
            onAnswer({
              qfs,
              skip: skipped,
            });
          });
        } else {
          onAnswer({
            qfs,
            skip: skipped,
          });
        }
      },
      [onAnswer, makeCodeForCurrentSession, createCode],
    );

    const answer: Answer = exchangeToAnswer(exchange?.type, exchange?.answer);

    const prevAnswerFromExchange = exchangeToAnswer(
      exchange?.type,
      exchange?.previousAnswer,
    );

    const exchangeQuestion = exchange?.question?.question;

    const hasInfoMessages = exchange?.info?.length > 0;

    const showQuestionElement = exchangeQuestion && (wasAnswered || onAnswer);

    const handleInfoMessageApproved = (index: number) => {
      setInfoIndexToShow(index + 1);
    };

    let serverError, networkError;
    if (exchange?.error && exchange.error["serverErrors"]?.length) {
      serverError = exchange?.error["serverErrors"][0];
    } else {
      networkError = exchange?.error;
    }

    return (
      <ChatElementContainer className={"chat-element-view"}>
        <Fade
          timeout={1200}
          in
          key={"chat-element" + index}
          mountOnEnter
          unmountOnExit
        >
          <div className={"fade-wrapper"}>
            {props.loading && (
              <LoadingContainer className={"loading-container"}>
                <DocQuestion text={""}>
                  <Loader color={PatientColorMap.Primary} />
                </DocQuestion>
              </LoadingContainer>
            )}
            {!!exchange && !props.loading && (
              <>
                {showQuestionElement && (
                  <QuestionElement
                    question={exchangeQuestion}
                    answer={answer}
                    index={index}
                    onAnswer={onAnswerCb}
                    prevAns={prevAnswerFromExchange}
                  />
                )}
                {wasAnswered && (
                  <AnswerElement
                    ref={ref}
                    gender={gender}
                    onUndo={undo}
                    exchange={{
                      type: exchange.type,
                      answer: exchange.answer,
                      question: exchangeQuestion,
                    }}
                  />
                )}
                {hasInfoMessages &&
                  exchange?.info
                    .slice(0, infoIndexToShow + 1)
                    .map((info, index) => (
                      <InfoMessageView
                        index={index}
                        info={info}
                        onIndexConfirm={handleInfoMessageApproved}
                        wasLastInfoApproved={!onAnswer}
                      />
                    ))}
                {serverError && (
                  <ServerErrorHandlerComponent error={serverError} />
                )}
                {networkError && <RefreshElement />}
              </>
            )}
          </div>
        </Fade>
      </ChatElementContainer>
    );
  }),
);
